import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { Checkbox } from '@components/ui/checkbox';
import { Button } from '@components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@components/ui/popover';
import { Calendar } from '@components/ui/calendar';
import { CalendarIcon, Loader2, CheckCircle2, Info } from 'lucide-react';
import { AgreementPanel } from './AgreementPanel';

interface ApproveSignCardProps {
  signatoryName: string;
  onSignatoryNameChange: (v: string) => void;
  signatoryTitle: string;
  onSignatoryTitleChange: (v: string) => void;
  signatureDate: Date | null;
  onSignatureDateChange: (d: Date | undefined) => void;
  authorizationConfirmed: boolean;
  onAuthorizationChange: (v: boolean) => void;
  showErrors: boolean;
  signatoryNameInvalid: boolean;
  signatoryTitleInvalid: boolean;
  signatureDateInvalid: boolean;
  authorizationInvalid: boolean;
  summaryMessage: string | null;
  onSubmit: () => void;
  onReset: () => void;
  submitting: boolean;
  submitStageError: string | null;
  rateChangedNotice: string | null;
  submissionSaved: { itemId: string; referenceNumber: string } | null;
}

export function ApproveSignCard(props: ApproveSignCardProps) {
  const {
    signatoryName,
    onSignatoryNameChange,
    signatoryTitle,
    onSignatoryTitleChange,
    signatureDate,
    onSignatureDateChange,
    authorizationConfirmed,
    onAuthorizationChange,
    showErrors,
    signatoryNameInvalid,
    signatoryTitleInvalid,
    signatureDateInvalid,
    authorizationInvalid,
    summaryMessage,
    onSubmit,
    onReset,
    submitting,
    submitStageError,
    rateChangedNotice,
    submissionSaved,
  } = props;

  const disableActions = submitting || Boolean(submissionSaved);

  return (
    <section className="mt-5 rounded-xl border border-border bg-card p-6 shadow-[var(--shadow-stack)] sm:p-8">
      <h2 className="font-[family-name:var(--font-heading)] text-xl font-medium text-foreground">Approve and sign</h2>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        Signing puts you in the queue for capacity at a ByFusion Diversion Hub. Desired start date is for planning
        purposes only. Formal start date, rates and transportation fees will be finalized in the service agreement
        that follows.
      </p>

      <AgreementPanel />

      <div className="mt-5">
        <Label htmlFor="signatory-name" className="text-xs font-medium text-foreground">
          Signature — type your full name
        </Label>
        <Input
          id="signatory-name"
          aria-invalid={showErrors && signatoryNameInvalid}
          className="mt-1.5 h-11 rounded-none border-x-0 border-t-0 border-b-2 bg-transparent px-1 font-[family-name:var(--font-heading)] text-lg italic shadow-none focus-visible:ring-0"
          value={signatoryName}
          onChange={(e) => onSignatoryNameChange(e.target.value)}
          placeholder="Full name"
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="signatory-title" className="text-xs font-medium text-foreground">
            Title
          </Label>
          <Input
            id="signatory-title"
            aria-invalid={showErrors && signatoryTitleInvalid}
            className="mt-1.5"
            value={signatoryTitle}
            onChange={(e) => onSignatoryTitleChange(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="signature-date" className="text-xs font-medium text-foreground">
            Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="signature-date"
                variant="outline"
                aria-invalid={showErrors && signatureDateInvalid}
                className="mt-1.5 w-full justify-start text-left font-normal tabular-nums"
              >
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                {signatureDate ? signatureDate.toLocaleDateString('en-US') : 'Select a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={signatureDate ?? undefined} onSelect={onSignatureDateChange} />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2.5">
        <Checkbox
          id="authorization"
          checked={authorizationConfirmed}
          onCheckedChange={(v) => onAuthorizationChange(v === true)}
          aria-invalid={showErrors && authorizationInvalid}
          className="mt-0.5"
        />
        <Label htmlFor="authorization" className="text-sm font-normal leading-relaxed text-foreground">
          I&apos;m authorized to sign for my company, and the volume and term above reflect what we plan to send.
        </Label>
      </div>

      {submissionSaved ? (
        <div className="mt-6 flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm text-foreground">
            Reservation saved — reference <span className="font-medium tabular-nums">{submissionSaved.referenceNumber}</span>.
            We&apos;re preparing your documents next.
          </p>
        </div>
      ) : rateChangedNotice ? (
        <div className="mt-6 flex items-start gap-2.5 rounded-lg border border-border bg-secondary/60 p-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-sm text-foreground">{rateChangedNotice}</p>
        </div>
      ) : submitStageError ? (
        <p className="mt-6 text-sm font-medium text-destructive">{submitStageError}</p>
      ) : showErrors && summaryMessage ? (
        <p className="mt-6 text-sm font-medium text-destructive">{summaryMessage}</p>
      ) : null}

      <div
        className={`flex flex-col gap-3 sm:flex-row sm:items-center ${
          submissionSaved || rateChangedNotice || submitStageError || (showErrors && summaryMessage) ? 'mt-3' : 'mt-6'
        }`}
      >
        <Button size="lg" onClick={onSubmit} disabled={disableActions} className="w-full sm:w-auto">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing your reservation…
            </>
          ) : submissionSaved ? (
            'Submitted'
          ) : (
            'Submit reservation'
          )}
        </Button>
        <Button size="lg" variant="ghost" onClick={onReset} disabled={submitting} className="w-full sm:w-auto">
          Start over
        </Button>
      </div>
    </section>
  );
}
